/**
 * DaddyTalkAvailabilityDrawer — V1.7.3 multi-step drawer.
 *
 * Step 1 ("discover"): search jobs / companies + 6 progressive-access
 *   quick-actions (Rate CV, Build resume, Save jobs, etc). No 1000
 *   recruiter dump.
 * Step 2 ("recruiters"): after picking a job, show the 3-5 recruiters
 *   who actually own it. Message CTA carries `context_type=job`
 *   context_id={job_id} so the recruiter sees WHICH role the candidate
 *   cares about the instant the thread lands in their inbox.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import api from "@/lib/api";
import {
    ChatCircleText, Sparkle, MagnifyingGlass, Briefcase, ArrowLeft,
    ArrowRight, Heart, FilePdf, ChalkboardTeacher, Lightning,
} from "@phosphor-icons/react";

export default function DaddyTalkAvailabilityDrawer({ open, onOpenChange }) {
    const [step, setStep] = useState("discover");   // "discover" | "recruiters"
    const [q, setQ] = useState("");
    const [jobs, setJobs] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [recruiters, setRecruiters] = useState([]);
    const [recLoading, setRecLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setStep("discover"); setSelectedJob(null);
        setQ(""); setRecruiters([]);
    }, [open]);

    // Debounced job search.
    useEffect(() => {
        if (step !== "discover" || !open) return;
        const h = setTimeout(async () => {
            setJobsLoading(true);
            try {
                const { data } = await api.get(
                    `/public/daddytalk/discover?q=${encodeURIComponent(q)}&limit=8`,
                );
                setJobs(data?.items || []);
            } catch { setJobs([]); }
            finally { setJobsLoading(false); }
        }, 250);
        return () => clearTimeout(h);
    }, [q, step, open]);

    const pickJob = async (job) => {
        setSelectedJob(job); setStep("recruiters"); setRecLoading(true);
        try {
            const { data } = await api.get(
                `/public/daddytalk/recruiters-for-job/${job.id}`,
            );
            setRecruiters(data?.items || []);
        } catch { setRecruiters([]); }
        finally { setRecLoading(false); }
    };

    // V1.7.9 — When a recruiter toggles boost, re-fetch so the pulse
    // + top-sort appear instantly for anyone viewing the drawer.
    useEffect(() => {
        if (!open || step !== "recruiters" || !selectedJob?.id) return;
        const onBoostChanged = async () => {
            try {
                const { data } = await api.get(
                    `/public/daddytalk/recruiters-for-job/${selectedJob.id}`,
                );
                setRecruiters(data?.items || []);
            } catch { /* silent */ }
        };
        window.addEventListener("daddytalk:boost-changed", onBoostChanged);
        window.addEventListener("daddytalk:my-boost-changed", onBoostChanged);
        return () => {
            window.removeEventListener("daddytalk:boost-changed", onBoostChanged);
            window.removeEventListener("daddytalk:my-boost-changed", onBoostChanged);
        };
    }, [open, step, selectedJob?.id]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" data-testid="daddytalk-availability-drawer"
                className="w-full sm:max-w-md bg-cream border-l-2 border-ink/10 overflow-y-auto p-0">
                <div className="bg-white border-b-2 border-ink/10 px-6 py-5">
                    {step === "recruiters" && (
                        <button
                            type="button"
                            onClick={() => setStep("discover")}
                            data-testid="daddytalk-drawer-back"
                            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-ink/50 hover:text-cobalt mb-2"
                        >
                            <ArrowLeft size={11} weight="bold" /> Back
                        </button>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-2">
                        <Sparkle size={11} weight="fill" /> DaddyTalk
                    </span>
                    <h2 className="font-heading text-2xl font-black leading-tight">
                        {step === "discover"
                            ? "What are you looking for?"
                            : `${selectedJob?.title || "Role"}`}
                    </h2>
                    {step === "recruiters" && (
                        <p className="text-xs text-ink/60 mt-1">
                            Recruiters handling this role — pick one to chat live.
                        </p>
                    )}
                </div>

                {step === "discover" && (
                    <div className="px-5 py-5 space-y-5">
                        <div className="relative">
                            <MagnifyingGlass size={14} weight="bold"
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                            <input
                                data-testid="daddytalk-drawer-search"
                                type="search" value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search a role, skill, or location…"
                                className="w-full pl-9 pr-3 py-3 bg-white border-2 border-ink/10 focus:border-cobalt outline-none text-sm"
                            />
                        </div>

                        {/* 6 progressive-access tiles */}
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600 mb-2">
                                Try before you sign up — no login
                            </p>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { to: "/rate-my-cv", label: "Rate CV", icon: FilePdf, testid: "rate-cv" },
                                    { to: "/resume", label: "Build CV", icon: Sparkle, testid: "resume" },
                                    { to: "/interview-questions", label: "Prep", icon: ChalkboardTeacher, testid: "prep" },
                                    { to: "/jobs", label: "Browse", icon: Briefcase, testid: "browse" },
                                    { to: "/jobs", label: "Save", icon: Heart, testid: "save" },
                                    { to: "/daddytalk", label: "Learn more", icon: ArrowRight, testid: "learn" },
                                ].map((t) => (
                                    <Link key={t.testid} to={t.to}
                                        onClick={() => onOpenChange(false)}
                                        data-testid={`daddytalk-drawer-tile-${t.testid}`}
                                        className="bg-white p-2.5 border-2 border-ink/10 hover:border-cobalt transition-colors text-center"
                                    >
                                        <t.icon size={16} weight="bold" className="mx-auto text-cobalt mb-1" />
                                        <span className="block text-[10px] font-black uppercase tracking-wider">
                                            {t.label}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-ink/50 mb-2">
                                {q ? "Matching roles" : "Popular right now"}
                            </p>
                            {jobsLoading ? (
                                <div className="space-y-2">
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} className="h-14 bg-white border border-ink/10 animate-pulse" />
                                    ))}
                                </div>
                            ) : jobs.length === 0 ? (
                                <p className="text-xs text-ink/50 bg-white p-4 border-2 border-dashed border-ink/15 text-center">
                                    {q ? "No matches — try a different role or skill." : "No open roles yet."}
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {jobs.map((j) => (
                                        <button key={j.id} type="button"
                                            onClick={() => pickJob(j)}
                                            data-testid={`daddytalk-drawer-job-${j.id}`}
                                            className="w-full text-left bg-white p-3 border-2 border-ink/10 hover:border-cobalt transition-colors"
                                        >
                                            <p className="text-sm font-bold truncate">{j.title}</p>
                                            <p className="text-[11px] text-ink/50 mt-0.5 truncate">
                                                {j.client_name || "—"}{j.location ? ` · ${j.location}` : ""}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {step === "recruiters" && (
                    <div className="px-5 py-5">
                        {recLoading ? (
                            <div className="grid grid-cols-2 gap-3">
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} className="bg-white border-2 border-ink/10 p-4 h-40 animate-pulse" />
                                ))}
                            </div>
                        ) : recruiters.length === 0 ? (
                            <div data-testid="daddytalk-drawer-rec-empty"
                                className="bg-white border-2 border-dashed border-ink/15 p-6 text-center">
                                <img src="/mascot/daddytalk/avatar_thinking.png" alt=""
                                    className="w-16 h-16 rounded-full mx-auto mb-3 border-4 border-white shadow-md" />
                                <h3 className="font-heading text-sm font-bold mb-1">
                                    No one live for this role right now
                                </h3>
                                <p className="text-ink/60 text-xs mb-4">
                                    Apply directly — we'll route your CV to the right person.
                                </p>
                                <Link to={`/apply/${selectedJob?.id}`}
                                    onClick={() => onOpenChange(false)}
                                    data-testid="daddytalk-drawer-rec-empty-apply"
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-cobalt text-white text-[11px] font-black uppercase tracking-[0.2em] hover:bg-cobalt-dark transition-colors">
                                    <Lightning size={12} weight="bold" /> Apply to this role
                                </Link>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3 pt-3">
                                {recruiters.map((r) => (
                                    <div key={r.id}
                                        data-testid={`daddytalk-drawer-rec-${r.id}`}
                                        data-online={r.is_online ? "1" : "0"}
                                        data-boosted={r.boosted ? "1" : "0"}
                                        className={`bg-white p-4 rounded-2xl transition-all duration-200 flex flex-col relative group hover:-translate-y-1 ${
                                            r.boosted
                                                ? "ring-2 ring-emerald-500 shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30"
                                                : r.is_online
                                                    ? "ring-1 ring-ink/10 shadow-sm hover:shadow-md hover:ring-emerald-300"
                                                    : "ring-1 ring-ink/10 hover:ring-ink/25 hover:shadow-sm"
                                        }`}>
                                        {r.boosted && (
                                            <span
                                                data-testid={`daddytalk-drawer-boosted-badge-${r.id}`}
                                                className="absolute -top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-semibold px-2.5 py-0.5 whitespace-nowrap rounded-full shadow-md"
                                            >
                                                <span className="relative flex h-1.5 w-1.5">
                                                    <span className="animate-radar absolute inline-flex h-full w-full rounded-full bg-white" />
                                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                                                </span>
                                                Live now
                                            </span>
                                        )}
                                        <div className="relative w-14 h-14 mx-auto mb-2">
                                            {r.avatar_url ? (
                                                <img src={r.avatar_url}
                                                    alt={r.name || "Recruiter"}
                                                    data-testid={`daddytalk-drawer-avatar-${r.id}`}
                                                    className="w-full h-full rounded-full object-cover border-4 border-cream"
                                                    onError={(e) => { e.currentTarget.src = "/mascot/daddytalk/avatar_neutral.png"; }} />
                                            ) : (
                                                <div
                                                    data-testid={`daddytalk-drawer-avatar-initial-${r.id}`}
                                                    className="w-full h-full rounded-full border-4 border-cream bg-gradient-to-br from-cobalt to-cobalt/70 text-white flex items-center justify-center font-heading font-black text-lg"
                                                >
                                                    {(r.name || "R").trim().charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            {r.boosted ? (
                                                <span className="absolute bottom-0 right-0 flex h-3.5 w-3.5">
                                                    <span className="animate-radar absolute inline-flex h-full w-full rounded-full bg-emerald-400" />
                                                    <span className="animate-radar-slow absolute inline-flex h-full w-full rounded-full bg-emerald-400" />
                                                    <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500 border-2 border-white" />
                                                </span>
                                            ) : (
                                                <span className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white ${r.is_online ? "bg-emerald-500" : "bg-amber-400"}`} />
                                            )}
                                        </div>
                                        <h3 className="font-heading text-xs font-bold text-center mb-0.5 truncate">
                                            {r.name}
                                        </h3>
                                        <p className={`text-[9px] text-center uppercase tracking-widest mb-3 font-black ${
                                            r.boosted ? "text-emerald-600" : r.is_online ? "text-emerald-600" : "text-amber-600"
                                        }`}>
                                            {r.boosted ? "● Recruiting now" : r.is_online ? "● Online" : "● Away"}
                                        </p>
                                        <Link to={`/daddytalk/chat/${r.id}?job=${selectedJob?.id}`}
                                            onClick={() => onOpenChange(false)}
                                            data-testid={`daddytalk-drawer-msg-${r.id}`}
                                            className="mt-auto w-full inline-flex items-center justify-center gap-1.5 bg-cobalt text-white text-[10px] font-black uppercase tracking-wider py-2 hover:bg-cobalt-dark transition-colors">
                                            <ChatCircleText size={10} weight="bold" /> Message
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
